"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MatchContext } from "@/lib/targeting/match";
import { type RankedSlot, topMessageSlot } from "@/lib/targeting/rank-slots";
import {
  dismissLapsesAt,
  isVariantRead,
  markVariantRead,
  markVariantShown,
  type ReadTrigger,
  readSlotState,
} from "@/lib/targeting/slot-read-state";
import type { VisitorStorage } from "@/lib/visitor-storage";
import type { MessageSlot } from "@/lib/widget-config";

/** The same grace the synthetic welcome used: a flow greeting that lands right after the handshake wins. */
export const LAUNCHER_MESSAGE_GRACE_MS = 1_500;
/** setTimeout overflows past 2^31 - 1 ms (about 24.8 days). */
const MAX_TIMER_MS = 2_000_000_000;

export interface LauncherMessage {
  /** The effective slots contain a MESSAGE slot, so the page-decides launcher rules apply. */
  active: boolean;
  /** The slot the CURRENT PAGE decides on (read or not): the launcher opens its thread. */
  top: RankedSlot | null;
  /** The bubble over the launcher: `top` while it is unread, else nothing. */
  shown: RankedSlot | null;
  /**
   * The top-ranked slot variant is unread (same read state as the bubble, whatever hides the bubble):
   * the launcher badge counts it as +1. Clears with the bubble's read triggers.
   */
  unread: boolean;
  /** Marks the shown variant read (a click, an X). Opening the panel is handled by the hook. */
  markRead: (trigger: Exclude<ReadTrigger, "open">) => void;
}

/**
 * Which launcher message shows, and the per-variant "read" behind it. The bubble and the launcher
 * always point at the SAME top-ranked slot; "read" only gates the bubble. A real unread thread's
 * peek (`suppressed`) wins over the bubble and reads nothing.
 */
export function useLauncherMessage(opts: {
  /** The session projection when it answered, else the public slots. */
  slots: readonly MessageSlot[];
  ctx: MatchContext;
  publicKey: string | null;
  /** Instance storage: memory for `session="isolated"` and preview, so nothing outlives the mount. */
  storage: VisitorStorage;
  /** Session content settled and the visitor's threads known: the grace starts now. */
  ready: boolean;
  isOpen: boolean;
  suppressed: boolean;
  graceMs?: number;
  now?: () => number;
  /** Dashboard preview (`launcherMessagePreview="visible"`): show the top matching slot whatever its read state. */
  previewVisible?: boolean;
  /** The visitor is blocked (contact level): no bubble and no badge count, live as well as on mount. */
  blocked?: boolean;
}): LauncherMessage {
  const { slots, ctx, publicKey, storage, ready, isOpen, suppressed } = opts;
  const previewVisible = opts.previewVisible === true;
  const graceMs = opts.graceMs ?? LAUNCHER_MESSAGE_GRACE_MS;
  const nowRef = useRef(opts.now ?? Date.now);
  nowRef.current = opts.now ?? Date.now;
  const pk = publicKey ?? "";

  const active = useMemo(() => slots.some((s) => s.kind === "MESSAGE"), [slots]);
  const top = useMemo(() => topMessageSlot(slots, ctx), [slots, ctx]);

  const [version, setVersion] = useState(0);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!ready) {
      setArmed(false);
      return;
    }
    const id = setTimeout(() => setArmed(true), graceMs);
    return () => clearTimeout(id);
  }, [ready, graceMs]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `version` is the explicit re-read trigger after a write to storage
  const read = useMemo(
    () =>
      top && !previewVisible
        ? isVariantRead(
            readSlotState(storage, pk, top.slotId),
            top.variant,
            top.reappearAfterDays,
            nowRef.current(),
          )
        : false,
    [top, storage, pk, version, previewVisible],
  );

  // A dismiss with reappearAfterDays lapses while the page sits idle too: wake up at that instant.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `version` re-arms the timer after a write
  useEffect(() => {
    if (!top || !read || previewVisible) return;
    const at = dismissLapsesAt(
      readSlotState(storage, pk, top.slotId),
      {
        id: top.variant.id,
        contentHash: top.variant.contentHash,
      },
      top.reappearAfterDays,
    );
    if (at === null) return;
    const id = setTimeout(
      () => setVersion((v) => v + 1),
      Math.min(Math.max(at - nowRef.current(), 0) + 50, MAX_TIMER_MS),
    );
    return () => clearTimeout(id);
  }, [top, read, storage, pk, version, previewVisible]);

  const allowed = opts.blocked !== true;
  const unread = allowed && armed && !!top && !read;
  const shown = allowed && armed && top && !read && !isOpen && !suppressed ? top : null;
  const shownRef = useRef(shown);
  shownRef.current = shown;

  const write = useCallback(
    (r: RankedSlot, trigger: ReadTrigger) => {
      if (previewVisible) return;
      const variant = { id: r.variant.id, contentHash: r.variant.contentHash };
      markVariantRead(storage, pk, r.slotId, variant, trigger, nowRef.current());
      setVersion((v) => v + 1);
    },
    [storage, pk, previewVisible],
  );

  // "Shown" is recorded the first time the bubble is displayed.
  useEffect(() => {
    if (!shown || previewVisible) return;
    markVariantShown(
      storage,
      pk,
      shown.slotId,
      { id: shown.variant.id, contentHash: shown.variant.contentHash },
      nowRef.current(),
    );
  }, [shown, storage, pk, previewVisible]);

  // Opening the panel while the bubble is visible reads it. The bubble is already gone from `shown`
  // in the render where `isOpen` flips, so the last visible one is remembered across commits.
  const visibleBeforeOpen = useRef<RankedSlot | null>(null);
  useEffect(() => {
    if (!isOpen) {
      visibleBeforeOpen.current = shown;
      return;
    }
    const seen = visibleBeforeOpen.current;
    visibleBeforeOpen.current = null;
    if (seen) write(seen, "open");
  }, [isOpen, shown, write]);

  const markRead = useCallback(
    (trigger: Exclude<ReadTrigger, "open">) => {
      const current = shownRef.current;
      if (current) write(current, trigger);
    },
    [write],
  );

  return useMemo(
    () => ({ active, top, shown, unread, markRead }),
    [active, top, shown, unread, markRead],
  );
}
